import axios from 'axios';
import * as Sentry from '@sentry/react';
import { getToken } from './auth';

const BASE_URL = process.env.REACT_APP_API_URL ?? '';

export const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = token;
  }
  return config;
});

// Response interceptor — capture non-2xx and network errors that the calling
// UI may gracefully handle (toast / fallback state). React's auto-error
// capture only sees uncaught exceptions; without this, a portal-wide API
// outage would be invisible in Sentry until something throws.
api.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status;
    const url = error?.config?.url ?? 'unknown';
    const method = (error?.config?.method ?? 'get').toUpperCase();
    if (status) {
      // 401 is "session expired" — noisy at scale, scope to warning so it
      // doesn't drown out real issues. 4xx ≥ 402 and 5xx are real signals.
      const level = status === 401 ? 'info' : status >= 500 ? 'error' : 'warning';
      Sentry.captureMessage(`API ${method} ${url} → ${status}`, {
        level,
        tags: { source: 'frontend_api', http_status: String(status), http_method: method },
        extra: { url, response: error?.response?.data },
      });
    } else {
      // No status = network error / CORS / DNS / offline
      Sentry.captureException(error, {
        tags: { source: 'frontend_api', failure: 'network' },
        extra: { url, method },
      });
    }
    return Promise.reject(error);
  },
);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DashboardStats {
  total: number;
  by_status: Record<string, number>;
  by_company: { name: string; count: number }[];
  credits_remaining: number;
  verifications_per_day: { date: string; count: number }[];
}

export interface Verification {
  scanRef: string;
  timestamp: string;
  status: string;
  full_name?: string;
  contact_name?: string;       // Freshdesk contact name (fallback for company grouping)
  contact_company_name?: string;
  contact_email?: string;
  ticket_id?: string;          // Freshdesk ticket number
  doc_type?: string;
  ip_country?: string;
}

export interface VerificationsResponse {
  verifications: Verification[];
  last_key?: string;
}

export interface ReportSettings {
  account_id: string;
  report_email: string;
  report_interval: '7d' | '14d' | '30d';
  language?: 'nl' | 'en';
}

export interface BillingPortalResponse {
  url: string | null;
  message?: string;
}

export interface BillingInfo {
  plan: 'trial' | 'starter' | 'msp_complete';
  subscription_status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  credits_remaining: number;
  plan_credits: number;
  auto_topup_quantity: number;
  auto_topup_consent: boolean;
  privacy_policy_signed: boolean;
  // Unix epoch when GDPR purge will execute (set when Freshdesk app is
  // uninstalled). null while installed. Used by the UninstallBanner.
  purge_scheduled_at: number | null;
}

export interface CheckoutRequest {
  plan: 'starter' | 'msp_complete';
  auto_topup_quantity: number;
  auto_topup_consent: boolean;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const fetchDashboard = (from: string, to: string) =>
  api.get<DashboardStats>('/api/dashboard', { params: { from, to } }).then(r => r.data);

export const fetchVerifications = (from: string, to: string, limit = 50, last_key?: string) =>
  api.get<VerificationsResponse>('/api/verifications', {
    params: { from, to, limit, last_key },
  }).then(r => r.data);

export const fetchReportSettings = () =>
  api.get<ReportSettings>('/api/report/settings').then(r => r.data);

export const saveReportSettings = (settings: Omit<ReportSettings, 'account_id'>) =>
  api.put<{ success: boolean }>('/api/report/settings', settings).then(r => r.data);

export interface ReportGeneratePdfResponse {
  format?: 'pdf';
  report_id: string;
  status: 'pending';
}

export interface ReportGenerateCsvResponse {
  format: 'csv';
  csv: string;
  filename: string;
}

export type ReportGenerateResponse = ReportGeneratePdfResponse | ReportGenerateCsvResponse;

export interface ReportStatusResponse {
  report_id: string;
  status: 'pending' | 'complete' | 'failed';
  file_url?: string;
  from?: string;
  to?: string;
}

export const generateReport = (
  from: string,
  to: string,
  language: 'nl' | 'en' = 'nl',
  format: 'pdf' | 'csv' = 'pdf',
): Promise<ReportGenerateResponse> =>
  api.post<ReportGenerateResponse>('/api/report/generate', { from, to, language, format }).then(r => r.data);

export const getReportStatus = (report_id: string): Promise<ReportStatusResponse> =>
  api.get<ReportStatusResponse>('/api/report/status', { params: { report_id } }).then(r => r.data);

export interface ReportDetailResponse {
  pdf_base64: string;
  scan_ref: string;
  language: string;
}

export const fetchReportDetail = (scan_ref: string, language: 'en' | 'lt' = 'en'): Promise<ReportDetailResponse> =>
  api.post<ReportDetailResponse>('/api/report/detail', { scan_ref, language }).then(r => r.data);

export const fetchBillingPortal = () =>
  api.get<BillingPortalResponse>('/api/billing/portal').then(r => r.data);

export const fetchBillingInfo = () =>
  api.get<BillingInfo>('/api/billing/info').then(r => r.data);

export const createCheckout = (req: CheckoutRequest) =>
  api.post<{ checkout_url: string }>('/api/billing/checkout', req).then(r => r.data);

export const signPrivacyPolicy = () =>
  api.post<{ signed: boolean; signed_at: string }>('/api/billing/policy/sign').then(r => r.data);

// ── Company ───────────────────────────────────────────────────────────────────

export interface CompanyData {
  company_name:        string;
  vat_number:          string;
  address_line1:       string;
  address_city:        string;
  address_postal_code: string;
  address_country:     string;
}

export const fetchCompany = () =>
  api.get<CompanyData>('/api/billing/company').then(r => r.data);

export const saveCompany = (data: CompanyData) =>
  api.put<{ saved: boolean }>('/api/billing/company', data).then(r => r.data);

// ── Invoices ──────────────────────────────────────────────────────────────────

export interface Invoice {
  id:          string;
  number:      string | null;
  date:        number;   // unix timestamp
  amount:      number;   // cents
  currency:    string;
  status:      string | null;
  pdf_url:     string | null;
  hosted_url:  string | null;
  period_from: number;
  period_to:   number;
}

export const fetchInvoices = () =>
  api.get<{ invoices: Invoice[] }>('/api/billing/invoices').then(r => r.data.invoices);

// ── Payment method ────────────────────────────────────────────────────────────

export interface PaymentMethod {
  id:        string;
  brand:     string;
  last4:     string;
  exp_month: number;
  exp_year:  number;
}

export const fetchPaymentMethod = () =>
  api.get<{ method: PaymentMethod | null }>('/api/billing/payment').then(r => r.data.method);

export const createSetupIntent = () =>
  api.post<{ client_secret: string }>('/api/billing/payment/setup-intent').then(r => r.data);

export const updatePaymentMethod = (payment_method_id: string) =>
  api.put<{ updated: boolean }>('/api/billing/payment', { payment_method_id }).then(r => r.data);
